import React, {useEffect, useState} from "react";
import {Box, Grid, MenuItem, Typography} from "@material-ui/core";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useConsumerContext} from "../../context/ConsumerContext";
import addressData from "../../data/addressData.json";
import {AVAILABLE_ACTIONS} from "../../utils/constants";
import * as yup from "yup";
import {MUICardWithControl, MUIInput, MUISelect} from "../../utils/formUtils";
import {WizardNav} from "../../utils/fmapUtils";
import SquareFootOutlinedIcon from '@mui/icons-material/SquareFootOutlined';
import RoofingOutlinedIcon from '@mui/icons-material/RoofingOutlined';
import ElectricalServicesOutlinedIcon from '@mui/icons-material/ElectricalServicesOutlined';
import PlumbingOutlinedIcon from '@mui/icons-material/PlumbingOutlined';
import EngineeringIcon from '@mui/icons-material/Engineering';
import LocationCityRoundedIcon from '@mui/icons-material/LocationCityRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';

const validationSchema = yup.object().shape({
    propertyType: yup.string().required('Property type required.').nullable().notOneOf(['none'],'Property type required'),
    constructionType: yup.string().required('Construction type required.').nullable().notOneOf(['none'],'Construction type required'),
    yearBuilt: yup.number().typeError('Enter a valid number.').required('Please complete all required fields').positive('Enter a valid number.').integer().min(1799, 'Enter a valid number.').max(new Date().getFullYear(), 'Year built cannot be in future'),
    roof: yup.string().when('yearBuilt', (yearBuilt) => {
        if (yearBuilt && new Date().getFullYear() - yearBuilt > 25 && yearBuilt > 999) {
            return yup.string().required('Enter a valid year.').notOneOf(['none'],'Enter a valid year.')
        } else {
            return yup.string().notRequired().nullable()
        }
    }),
    electrical: yup.string().when('yearBuilt', (yearBuilt) => {
        if (yearBuilt && new Date().getFullYear() - yearBuilt > 25 && yearBuilt > 999) {
            return yup.string().required('Enter a valid year.').notOneOf(['none'],'Enter a valid year.')
        } else {
            return yup.string().notRequired().nullable()
        }
    }),
    plumbing: yup.string().when('yearBuilt', (yearBuilt) => {
        if (yearBuilt && new Date().getFullYear() - yearBuilt > 25 && yearBuilt > 999) {
            return yup.string().required('Enter a valid year.').notOneOf(['none'],'Enter a valid year.')
        } else {
            return yup.string().notRequired().nullable()
        }
    }),
    squareFootage: yup.number().typeError('Enter a valid number.').required('Enter a valid number.').positive('Enter a valid number.').integer().min(1, 'Enter a valid number.').max(9999999)
});
export default function PropertyDetails() {
    const {handleSubmit, control, trigger, formState: {errors}} = useForm({resolver: yupResolver(validationSchema)});
    const {consumerData, dispatch} = useConsumerContext();

    // loading static data when component loads
    useEffect(() => {
        // Anything in here is fired on component mount.
        // fetching static data
        setPropertyTypeList(addressData.propertyType);
        setConstructionTypeList(addressData.constructionType);
        setYearSpanList(addressData.yearSpanConsumer);
    }, []);

    const [propertyTypeList, setPropertyTypeList] = useState([]);
    const [constructionTypeList, setConstructionTypeList] = useState([]);
    const [yearSpanList, setYearSpanList] = useState([]);

    // updating the state as the user types...
    const handleFormChange = (event) => {
        dispatch({
            type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_DETAILS,
            userRequest: {[event.target.name]: event.target.value}
        });
    };

    const onSubmit = (data) => {
        conditionalCheckForYearBuilt(data);
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_DETAILS, userRequest: data});
        dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep + 1});
    };

    const handleBackClick = async () => {
        conditionalCheckForYearBuilt();
        const isValid = await trigger(['propertyType', 'constructionType', 'yearBuilt', 'roof', 'electrical', 'plumbing', 'squareFootage']);
        if (isValid) {
            dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep - 1});
        }
    }

    function conditionalCheckForYearBuilt(data) {
        if (!consumerData?.yearBuilt > 999) {
            if (data) {
                data.roof = undefined;
                data.electrical = undefined;
                data.plumbing = undefined;
                // data.squareFootage = undefined;
            } else {
                dispatch({type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_DETAILS, userRequest: {"roof": "none"}});
                dispatch({type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_DETAILS, userRequest: {"electrical": "none"}});
                dispatch({type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_DETAILS, userRequest: {"plumbing": "none"}});
                // dispatch({type: AVAILABLE_ACTIONS.UPDATE_PROPERTY_DETAILS, userRequest: {"squareFootage":undefined}});

            }
        }
    }

    return (
        <section>
            <Box m={2} marginBottom={0}>
                <Typography variant="h4" align="left" component="h4" gutterBottom
                            style={{paddingLeft: 24, fontWeight: 600}}>
                    Property Details
                </Typography>
            </Box>
            <form
                onSubmit={handleSubmit(onSubmit)}>
                <Box m={3} marginTop={0}>
                    <Grid container alignItems="flex-start" spacing={2} style={{paddingLeft: 8, paddingTop: 8}}>
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="Property Type" children={
                                <MUISelect id="propertyType" control={control} name="propertyType"
                                           errors={errors}
                                           defaultValue={consumerData?.propertyType}
                                           value={consumerData?.propertyType} onFormChange={handleFormChange}>
                                    <MenuItem key="none" value="none" disabled>--Select--</MenuItem>
                                    {propertyTypeList.map(type => {
                                        return <MenuItem key={type?.value} value={type?.value}>{type?.label}</MenuItem>
                                    })}
                                </MUISelect>
                            } icon={<LocationCityRoundedIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="Construction Type" children={
                                <MUISelect id="constructionType" control={control} name="constructionType"
                                           errors={errors}
                                           defaultValue={consumerData?.constructionType}
                                           value={consumerData?.constructionType} onFormChange={handleFormChange}>
                                    <MenuItem key="none" value="none" disabled>--Select--</MenuItem>
                                    {constructionTypeList.map(type => {
                                        return <MenuItem key={type?.value} value={type?.value}>{type?.label}</MenuItem>
                                    })}
                                </MUISelect>
                            } icon={<EngineeringIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="Year Built" children={
                                <MUIInput control={control} name="yearBuilt" inputPrp={{maxLength: 4}}
                                          defaultValue={consumerData?.yearBuilt}
                                          value={consumerData?.yearBuilt}
                                          onInputChange={handleFormChange} errors={errors}></MUIInput>
                            } icon={<TodayRoundedIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                        {consumerData?.yearBuilt > 999 &&
                        <>
                            <Grid item xs={12} sm={6} md={4}>
                                <MUICardWithControl label="Year Roof Updated" children={
                                    <MUISelect id="roof" control={control} name="roof" errors={errors}
                                               defaultValue={consumerData?.roof}
                                               value={consumerData?.roof} onFormChange={handleFormChange}>
                                        <MenuItem key="none" value="none" disabled>--Select--</MenuItem>
                                        {yearSpanList.map(type => {
                                            return <MenuItem key={type?.value}
                                                             value={type?.value}>{type?.label}</MenuItem>
                                        })}
                                    </MUISelect>
                                } icon={<RoofingOutlinedIcon style={{
                                    backgroundColor: '#3f51b5',
                                    padding: 16,
                                    fontSize: 16,
                                    height: 64,
                                    width: 64,
                                    marginBottom: 16,
                                    color: '#ffffff',
                                    borderRadius: 20
                                }}/>}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <MUICardWithControl label="Year Electrical Updated" children={
                                    <MUISelect id="electrical" control={control} name="electrical" errors={errors}
                                               defaultValue={consumerData?.electrical}
                                               value={consumerData?.electrical} onFormChange={handleFormChange}>
                                        <MenuItem key="none" value="none" disabled>--Select--</MenuItem>
                                        {yearSpanList.map(type => {
                                            return <MenuItem key={type?.value}
                                                             value={type?.value}>{type?.label}</MenuItem>
                                        })}
                                    </MUISelect>
                                } icon={<ElectricalServicesOutlinedIcon style={{
                                    backgroundColor: '#3f51b5',
                                    padding: 16,
                                    fontSize: 16,
                                    height: 64,
                                    width: 64,
                                    marginBottom: 16,
                                    color: '#ffffff',
                                    borderRadius: 20
                                }}/>}/>
                            </Grid>
                            <Grid item xs={12} sm={6} md={4}>
                                <MUICardWithControl label="Year Plumbing Updated" children={
                                    <MUISelect id="plumbing" control={control} name="plumbing" errors={errors}
                                               defaultValue={consumerData?.plumbing}
                                               value={consumerData?.plumbing} onFormChange={handleFormChange}>
                                        <MenuItem key="none" value="none" disabled>--Select--</MenuItem>
                                        {yearSpanList.map(type => {
                                            return <MenuItem key={type?.value}
                                                             value={type?.value}>{type?.label}</MenuItem>
                                        })}
                                    </MUISelect>
                                } icon={<PlumbingOutlinedIcon style={{
                                    backgroundColor: '#3f51b5',
                                    padding: 16,
                                    fontSize: 16,
                                    height: 64,
                                    width: 64,
                                    marginBottom: 16,
                                    color: '#ffffff',
                                    borderRadius: 20
                                }}/>}/>
                            </Grid>
                        </>
                        }
                        <Grid item xs={12} sm={6} md={4}>
                            <MUICardWithControl label="Square Footage" children={
                                <MUIInput control={control} name="squareFootage" inputPrp={{maxLength: 7}}
                                          defaultValue={consumerData?.squareFootage}
                                          value={consumerData?.squareFootage}
                                          onInputChange={handleFormChange} errors={errors}></MUIInput>
                            } icon={<SquareFootOutlinedIcon style={{
                                backgroundColor: '#3f51b5',
                                padding: 16,
                                fontSize: 16,
                                height: 64,
                                width: 64,
                                marginBottom: 16,
                                color: '#ffffff',
                                borderRadius: 20
                            }}/>}/>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}></Grid>
                    </Grid>
                </Box>
                <WizardNav currentStep={consumerData?.wizardStep} handleBackClick={handleBackClick}/>
            </form>
        </section>
    );
}
